import React, {useMemo} from "react";
import styled from "styled-components";
import {
  ModalFieldContainer,
  ModalText,
  ModalTitle,
} from "../../common/components/modules/modal/modalComponents";
import {formatPrice} from "src/core/common/price";
import dayjs from "dayjs";
import DeliveryTypeBadge from "../../deliveries/components/DeliveryTypeBadge";
import useUserOrderPath from "../hooks/useUserOrderPath";
import Rating from "../../common/components/modules/Rating";
import useAuthentication from "src/core/authentication/hooks/useAuthentication";
import OutlinedButton from "../../common/components/elements/button/OutlinedButton";
import usePaymentOptions from "../../checkout/hooks/usePaymentOptions";
import useFeatureToggles from "../../common/hooks/useFeatureToggles";
import {Close} from "styled-icons/remix-line/Close";
import {Check} from "styled-icons/remix-line/Check";
import {ErrorWarning} from "@styled-icons/remix-line/ErrorWarning";
import media from "src/core/common/themes/media";
import AddressComponent from "src/core/checkout/components/AddressComponent";

export function OrderCard({order}) {
  const [, goToUserOrder, goToUserOrderRating, goToUserOrderTip] = useUserOrderPath();
  const address = order.getDeliveryAddress();
  const [, authApi] = useAuthentication();
  const user = authApi.userProfile();
  const deliveryType = order.getDeliveryType();
  const deliveryMode = order.getDeliverySpecification().getMode();
  const [paymentOptions] = usePaymentOptions();
  const toggles = useFeatureToggles();

  const cardTitle = order.getDisplayStatus();

  const paymentOption = useMemo(() => {
    if (!order.getPaymentOption()) return null;
    return paymentOptions.find(opt => opt.getType() === order.getPaymentOption());
  }, [order, paymentOptions]);

  const allowTipping =
    toggles.allowTips() &&
    order &&
    order.isCompleted() &&
    order.isOnlinePayment() &&
    !order.hasTip() &&
    order.hasReviews();

  const statusMessage = order && order.getStatusMessage();
  return (
    <OrderCardContainer>
      <OrderStatus hasStatusMessage={statusMessage}>
        <DeliveryTypeBadge deliveryType={deliveryType} deliveryMode={deliveryMode} />
        <ModalTitle>{cardTitle}</ModalTitle>
        {order.isPaid() && paymentOption && (
          <ModalTitle>Paid with {paymentOption.getName()}</ModalTitle>
        )}
        <AddressComponent
          address={address}
          addressee={
            order.getDeliveryType().pickupAtShop()
              ? order.getStoreName()
              : user
              ? user.getName()
              : ""
          }
        />
        <OrderStatusFooter statusMessage={statusMessage} />
      </OrderStatus>

      <OrderDetails>
        <OrderInfo>
          <OrderDateTitle>
            <div>{dayjs(order.getDeliveryDate()).format("MMM DD, YYYY")}</div>
            <div>{order.getScheduleTimeRangeString()}</div>
          </OrderDateTitle>
          <ModalFieldContainer>
            <ModalTitle>Order number:</ModalTitle>
            <ModalText>{order.getOrderNumber()}</ModalText>
          </ModalFieldContainer>
          <ModalFieldContainer>
            <ModalTitle>Items:</ModalTitle>
            <ModalText>{order.getItemsCount()}</ModalText>
          </ModalFieldContainer>
          <ModalFieldContainer>
            <ModalTitle>Order total:</ModalTitle>
            <ModalText>{formatPrice(order.getTotal())}</ModalText>
          </ModalFieldContainer>
        </OrderInfo>

        <OrderActions>
          {order.isCompleted() && !order.hasReviews() && (
            <OutlinedButton
              data-cy="startRatingOrderButton"
              onClick={() => goToUserOrderRating(order.getId())}
              label="Rate your experience"
            />
          )}
          {order.hasReviews() && (
            <Rating readonly={true} initialRating={order.getFirstReview().rating} />
          )}
          {allowTipping && (
            <OutlinedButton
              onClick={() => goToUserOrderTip(order.getId())}
              label="Show support with a tip"
            />
          )}
          <OutlinedButton
            onClick={() => goToUserOrder(order.getId())}
            label={order.isPendingPayment() ? "Pay Order" : "View this order"}
          />
        </OrderActions>
      </OrderDetails>
    </OrderCardContainer>
  );
}

function OrderStatusFooter({statusMessage}) {
  if (!statusMessage) return null;
  return (
    <StatusFooterContainer>
      <StatusIndicatorContainer>
        {statusMessage.status === "success" && <CheckIcon />}
        {statusMessage.status === "error" && <ErrorIcon />}
        {statusMessage.status === "warning" && <WarningIcon />}
      </StatusIndicatorContainer>
      <StatusRightSideContainer>
        <StatusName>{statusMessage.title}</StatusName>
        {statusMessage.description && (
          <StatusDescription>{statusMessage.description}</StatusDescription>
        )}
      </StatusRightSideContainer>
    </StatusFooterContainer>
  );
}

const ErrorIcon = styled(Close)`
  height: 20px;
  width: 20px;
  color: #fff;
  background-color: #fe4850;
  border-radius: 40px;
`;

const WarningIcon = styled(ErrorWarning)`
  height: 20px;
  width: 20px;
  color: #fff;
  background-color: #e9be69;
  border-radius: 40px;
`;

const CheckIcon = styled(Check)`
  height: 20px;
  width: 20px;
  color: #fff;
  background-color: #00cd32;
  border-radius: 40px;
`;

const StatusFooterContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  background-color: #fafafa;
  padding: 16px;
  column-gap: 16px;
  border-top: 1px solid #ebebeb;
  font-size: ${({theme}) => theme.v2.typography.sizing.m.lg};
  align-items: center;
`;

const StatusRightSideContainer = styled.div`
  display: flex;
  row-gap: 4px;
  flex-direction: column;
  align-items: start;
`;
const StatusIndicatorContainer = styled.div``;
const StatusName = styled.div`
  font-weight: ${({theme}) => theme.v2.typography.body.weight.heavy};
`;
const StatusDescription = styled.div.attrs(() => ({
  className: "order-status-footer__description-container",
}))`
  text-align: start;
`;

const OrderCardContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  padding: 0;
  > *:not(:last-child) {
    margin-right: 25px;
  }

  ${media.down("md")} {
    flex-direction: column;
    > *:not(:last-child) {
      margin-bottom: 20px;
      margin-right: 0;
    }
    &::-webkit-scrollbar {
      -webkit-appearance: none;
    }
  }
`;

const OrderDetails = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
`;

const OrderStatus = styled.div`
  display: flex;
  flex-direction: column;
  width: 99%;
  height: auto;
  text-align: center;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({theme}) => theme.v2.color.base.grey["100"]};
  border-radius: ${({theme}) => theme.v2.decoration.border.radius.default};
  padding: ${({hasStatusMessage}) => (hasStatusMessage ? "30px 0 0" : "30px 0")};

  > *:not(:last-child) {
    margin-bottom: ${({theme}) => theme.v2.spacing(4)};
  }
`;

const OrderActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  > *:not(:last-child) {
    margin-bottom: ${({theme}) => theme.v2.spacing(2)};
  }
`;

const OrderInfo = styled.div`
  margin-bottom: 20px;
`;

const OrderDateTitle = styled.div`
  font-size: ${({theme}) => theme.v2.typography.sizing["4xl"].lg};
  font-family: ${({theme}) => theme.v2.typography.titles.family};
  font-weight: ${({theme}) => theme.v2.typography.bodyText1.weight.heavy};
  color: ${({theme}) => theme.v2.color.base.grey["900"]};
  margin-bottom: ${({theme}) => theme.v2.spacing(2)};
`;
